import { type ReactNode } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { DEFAULT_TWC_PRICE, messages } from './messages';
import { type OptInEventContext } from './opt-in-analytics';
import {
	buildTwcFeatures,
	FeatureList,
	LegalText,
	OptInHeading,
	OptInModalLayout,
	OptInSubtitle,
	TwcLogos,
	useOptInModalViewed,
	useOptInSelect,
} from './opt-in-shared';
import { type ChoiceValue } from './types';
import { type LoomLandOptInCohort } from './use-loom-land-opt-in';
import { type LoomEdition, type TwcPriceMode } from './use-twc-price';

/**
 * TWCG-2108 Loom Land opt-in — "Variant B" design.
 *
 * A single-option variant (no radio choice): it presents only the Teamwork
 * Collection pitch with a primary CTA and a secondary Loom-only CTA, on the same
 * 50/50 modal as Variant A. The modal shell, analytics, header, features, legal
 * copy and graphic panel are shared via `opt-in-shared`.
 */

const CONTENT_TEST_ID = 'loom-land-opt-in-variant-b';

// Figma "Information Container": headline, features and legal, 20px apart.
const informationCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.250'),
});

// Figma "Headline": heading and subtitle, 12px apart.
const headlineCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
});

// Actions aligned bottom-right and pinned to the bottom of the column.
const actionsCss = css({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.050'),
	marginBlockStart: 'auto',
});

export type LoomChoiceVariantBProps = {
	/**
	 * Optional callback invoked when the user confirms via a CTA, before the
	 * redirect to signup/provisioning (e.g. for analytics). The CTA itself always
	 * navigates to the choice's continue URL — pass a custom {@link onSelect} only
	 * to hook additional behaviour, not to override routing.
	 */
	onSelect?: (choice: ChoiceValue) => void;
	/**
	 * Monthly per-user upsell price shown in the pricing feature line. Passed as
	 * an ICU argument so the amount (or currency) can change without editing the
	 * translated copy. Defaults to {@link DEFAULT_TWC_PRICE}.
	 */
	price?: ReactNode;
	/** Price message to render for {@link price}; defaults to "more". */
	priceMode?: TwcPriceMode;
	/** Loom edition, used to pick the provisioned TWC edition; defaults to "premium". */
	edition?: LoomEdition;
	/** Original page query string, passed through to the Loom-only signup redirect. */
	search?: string;
	/** Experiment cell, attached to analytics for cohort joins. */
	cohort?: LoomLandOptInCohort;
	/**
	 * Override the Loom clip played over the graphic panel on load. Defaults to
	 * the production clip; primarily for previewing it locally (e.g. Storybook).
	 */
	previewVideoSrc?: string;
};

export const LoomChoiceVariantB = ({
	onSelect,
	price = DEFAULT_TWC_PRICE,
	priceMode = 'more',
	edition = 'premium',
	search = '',
	cohort,
	previewVideoSrc,
}: LoomChoiceVariantBProps) => {
	const analyticsContext: OptInEventContext = { variant: 'b', cohort, edition, priceMode };
	const handleSelect = useOptInSelect({ onSelect, context: analyticsContext, search });
	useOptInModalViewed(analyticsContext);

	return (
		<OptInModalLayout
			contentTestId={CONTENT_TEST_ID}
			contentLayout="single"
			activeIllustration="teamwork-collection"
			previewVideoSrc={previewVideoSrc}
		>
			<TwcLogos />

			<div css={informationCss}>
				<div css={headlineCss}>
					<OptInHeading />
					<OptInSubtitle message={messages.twcSubtitle} />
				</div>
				<FeatureList features={buildTwcFeatures(price, priceMode)} size="medium" />
				<LegalText message={messages.legalTwc} />
			</div>

			<div css={actionsCss}>
				<Button appearance="subtle" onClick={() => handleSelect('loom-only')}>
					<FormattedMessage {...messages.variantBLoomOnlyCta} />
				</Button>
				<Button appearance="primary" onClick={() => handleSelect('teamwork-collection')}>
					<FormattedMessage {...messages.twcCta} />
				</Button>
			</div>
		</OptInModalLayout>
	);
};
